// Derivation helpers for the six PMS noyaux. Each noyau carries an integer
// weight (ponderation); its percentage is a pure function of its share of the
// total weight — there is no separately stored percentage. All helpers are pure
// so they can be unit-tested and reused by the RoundTagGroup component.
import { NOYAU_CODE_TO_KEY } from '@/lib/word-links';
import type { NoyauCode, NoyauKey, NoyauPonderation } from '@/schemas/word';

/** The six noyaux in tuple order — index matches the noyauPonderation tuple (and the `ponderation_<noyau>` columns). Not a display order; see {@link NOYAU_DISPLAY_ORDER}. */
export const NOYAU_ORDER: { code: NoyauCode; key: NoyauKey; label: string }[] = [
  { code: 'SGP', key: 'bleunuit', label: 'SGP' },
  { code: 'SPG', key: 'bleuciel', label: 'SPG' },
  { code: 'PSG', key: 'vert', label: 'PSG' },
  { code: 'PGS', key: 'jaune', label: 'PGS' },
  { code: 'GPS', key: 'rouge', label: 'GPS' },
  { code: 'GSP', key: 'rose', label: 'GSP' },
];

/**
 * The six noyaux in the canonical DISPLAY order (GSP, GPS, PGS, PSG, SPG,
 * SGP — the reverse of {@link NOYAU_ORDER}), used for fixed-order rendering
 * (noyau edit mode, reasoning steps). `index` points back into the
 * noyauPonderation tuple — the tuple's own index order is unchanged.
 */
export const NOYAU_DISPLAY_ORDER: {
  code: NoyauCode;
  key: NoyauKey;
  label: string;
  index: number;
}[] = NOYAU_ORDER.map((n, index) => ({ ...n, index })).reverse();

export interface NoyauDisplayEntry {
  code: NoyauCode;
  key: NoyauKey;
  label: string;
  /** Index into the noyauPonderation tuple. */
  index: number;
  weight: number;
  pct: number;
}

/** Continuous size bounds: 0% maps to `min`, 100% to `max`. */
export const RTG_PX = { min: 24, max: 56 } as const;
export const RTG_FONT_PX = { min: 9, max: 14 } as const;

/** An empty (all-zero) ponderation tuple. */
export function emptyNoyauPonderation(): NoyauPonderation {
  return [0, 0, 0, 0, 0, 0];
}

/** Sum of the six noyau weights. */
export function totalPonderation(p: NoyauPonderation): number {
  return p.reduce((sum, n) => sum + n, 0);
}

/**
 * Each noyau's percentage (0–100, rounded to an integer), derived from its share
 * of the total weight. Returns all zeros when the total is zero.
 */
export function noyauPercentages(p: NoyauPonderation): number[] {
  const total = totalPonderation(p);
  if (total === 0) return p.map(() => 0);
  return p.map((n) => Math.round((n / total) * 100));
}

/** The six noyaux in canonical DISPLAY order, each carrying its weight/pct for `p`. */
export function noyauDisplayEntries(p: NoyauPonderation): NoyauDisplayEntry[] {
  const percentages = noyauPercentages(p);
  return NOYAU_DISPLAY_ORDER.map((n) => ({
    ...n,
    weight: p[n.index] ?? 0,
    pct: percentages[n.index] ?? 0,
  }));
}

/**
 * View-mode order: weight descending, ties broken by the canonical DISPLAY
 * order (relies on `Array.prototype.sort`'s guaranteed stability to preserve
 * {@link NOYAU_DISPLAY_ORDER}'s input order among equal weights).
 */
export function sortedForDisplay(p: NoyauPonderation): NoyauDisplayEntry[] {
  return [...noyauDisplayEntries(p)].sort((a, b) => b.weight - a.weight);
}

/**
 * Index of the noyau with the highest weight. Ties go to the earliest in
 * canonical order. Returns null when every weight is zero.
 */
export function dominantNoyauIndex(p: NoyauPonderation): number | null {
  let bestIdx = -1;
  let best = 0;
  p.forEach((n, i) => {
    if (n > best) {
      best = n;
      bestIdx = i;
    }
  });
  return bestIdx === -1 ? null : bestIdx;
}

/**
 * The dominant noyau code for a word: the highest-weighted noyau, or the curated
 * `fallback` (the backend `noyau_sing`) when every weight is zero — the hybrid
 * rule agreed in the grill. Null when neither yields a code.
 */
export function dominantNoyauCode(
  p: NoyauPonderation,
  fallback: NoyauCode | null,
): NoyauCode | null {
  const idx = dominantNoyauIndex(p);
  if (idx === null) return fallback;
  return NOYAU_ORDER[idx]?.code ?? fallback;
}

/** The colour key for a word's dominant noyau (see {@link dominantNoyauCode}), or null. */
export function dominantNoyauKey(p: NoyauPonderation, fallback: NoyauCode | null): NoyauKey | null {
  const code = dominantNoyauCode(p, fallback);
  return code ? NOYAU_CODE_TO_KEY[code] : null;
}

function lerp(min: number, max: number, pct: number): number {
  const clamped = Math.max(0, Math.min(100, pct));
  return Math.round(min + (clamped / 100) * (max - min));
}

/** Linear interpolation of a percentage (0–100) to a circle diameter in px. */
export function pctToPx(pct: number): number {
  return lerp(RTG_PX.min, RTG_PX.max, pct);
}

/** Linear interpolation of a percentage (0–100) to a font size in px. */
export function pctToFontPx(pct: number): number {
  return lerp(RTG_FONT_PX.min, RTG_FONT_PX.max, pct);
}

/** ISO calendar date (YYYY-MM-DD) for today. */
export function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

// ── Immutable mutations (used by RoundTagGroup's −/+ controls) ────────────────

/** Increment the weight at `idx` by one, returning a new tuple. */
export function incrementAt(p: NoyauPonderation, idx: number): NoyauPonderation {
  return p.map((n, i) => (i === idx ? n + 1 : n)) as NoyauPonderation;
}

/** Decrement the weight at `idx` by one (floored at zero), returning a new tuple. */
export function decrementAt(p: NoyauPonderation, idx: number): NoyauPonderation {
  return p.map((n, i) => (i === idx ? Math.max(0, n - 1) : n)) as NoyauPonderation;
}

/**
 * A single-noyau ponderation tuple (weight 1 at `code`'s index, 0 elsewhere).
 * Used by read-only display contexts (search results, mosaic cards) to render
 * one fallback pastille from `noyau_sing` when a word's real ponderation is
 * all zero — never used in editable contexts, where the true (possibly
 * all-zero) weights must be shown as-is.
 */
export function singleNoyauPonderation(code: NoyauCode): NoyauPonderation {
  const idx = NOYAU_ORDER.findIndex((n) => n.code === code);
  return NOYAU_ORDER.map((_, i) => (i === idx ? 1 : 0)) as NoyauPonderation;
}

/**
 * The ponderation to render in read-only display contexts: the real weights,
 * or a single fallback pastille from `fallbackCode` (typically `noyau_sing`,
 * via `dominantNoyauCode` — which already reduces to it when every weight is
 * zero) when there is nothing to show otherwise.
 */
export function displayNoyauPonderation(
  p: NoyauPonderation,
  fallbackCode: NoyauCode | null,
): NoyauPonderation {
  if (totalPonderation(p) > 0 || !fallbackCode) return p;
  return singleNoyauPonderation(fallbackCode);
}

/**
 * PMS base/tint CSS variables for each noyau colour key — used to accent a
 * mosaic card with its dominant noyau's colour (see {@link dominantNoyauKey}),
 * either as a tinted background or a coloured border.
 */
export const NOYAU_KEY_VARS: Record<NoyauKey, { base: string; tint: string }> = {
  bleunuit: { base: 'var(--color-pms-bleu-nuit)', tint: 'var(--color-pms-bleu-nuit-tint)' },
  bleuciel: { base: 'var(--color-pms-bleu-ciel)', tint: 'var(--color-pms-bleu-ciel-tint)' },
  vert: { base: 'var(--color-pms-vert)', tint: 'var(--color-pms-vert-tint)' },
  jaune: { base: 'var(--color-pms-jaune)', tint: 'var(--color-pms-jaune-tint)' },
  rouge: { base: 'var(--color-pms-rouge)', tint: 'var(--color-pms-rouge-tint)' },
  rose: { base: 'var(--color-pms-rose)', tint: 'var(--color-pms-rose-tint)' },
};

/**
 * CSS class suffix for a word's `fonction` first letter. Not noyau-derived —
 * S/G/P is a separate vocabulary — but coloured with the same PMS tokens:
 * blue (bleu-nuit) for S, red (rouge) for G, green (vert) for P.
 */
export function fonctionLetterClass(letter: string): string {
  switch (letter) {
    case 'S':
      return 'words-res-fn--s';
    case 'G':
      return 'words-res-fn--g';
    case 'P':
      return 'words-res-fn--p';
    default:
      return '';
  }
}
